import { useEffect, useState } from "react";
import { Button, Checkbox, List, Typography, message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { getInstanceStatus } from "../../utility/common/common";

const { Title, Text } = Typography;

export interface Instance {
  instance_id: string;
  template_name: string;
  cpu: string;
  status: 0 | 1 | 2;
  product_name: string;
  node_name: string;
  internal_ip: string;
  healthy: number;
}

interface MeshInstanceGroupManagerProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (instances: Instance[]) => void;
  availableInstances: Instance[];
  clusterName: string;
  existingInstances: Instance[];
  onReclaimInstances?: (instances: Instance[]) => void;
}

export const MeshInstanceGroupManager = ({
  visible,
  onClose,
  onConfirm,
  availableInstances,
  clusterName,
  existingInstances,
}: MeshInstanceGroupManagerProps) => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [localAvailable, setLocalAvailable] = useState<Instance[]>([]);
  const [localSelected, setLocalSelected] = useState<Instance[]>([]);

  useEffect(() => {
    if (visible) {
      setLocalAvailable([...availableInstances]);
      setSelectedIds([]);
      setLocalSelected(existingInstances);
    }
  }, [visible, availableInstances]);

  const moveToCluster = () => {
    const selected = localAvailable.filter((i) =>
      selectedIds.includes(i.instance_id)
    );
    setLocalSelected((prev) => [...prev, ...selected]);
    setLocalAvailable((prev) =>
      prev.filter((i) => !selectedIds.includes(i.instance_id))
    );
    setSelectedIds([]);
  };

  const removeLocalSelected = (id: string) => {
    const instanceToRemove = localSelected.find((i) => i.instance_id === id);
    if (instanceToRemove) {
      setLocalSelected((prev) => prev.filter((i) => i.instance_id !== id));
      setLocalAvailable((prev) => [...prev, instanceToRemove]);
    }
  };

  const handleCheckboxChange = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((sid) => sid !== id)
    );
  };

  const handleConfirm = () => {
    onConfirm(localSelected);
    message.success(t("Instance group updated successfully."));
    onClose();
  };

  const isConfirmDisabled = localSelected.length === 0;

  return (
    <Modal
      title={t("Add Instances")}
      width={"90%"}
      open={visible}
      onCancel={onClose}
      footer={
        <div className="custom-modal-footer">
          <button className="secondary-btn" onClick={onClose}>
            {t("cancel")}
          </button>
          <button
            className="primary-btn"
            disabled={isConfirmDisabled}
            onClick={handleConfirm}
          >
            {t("Confirm")}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", gap: 24 }}>
        <div style={{ flex: 1 }}>
          <Title level={5}>{t("Instances")}</Title>
          {localAvailable.length === 0 ? (
            <Text type="secondary">{t("No instances available to add.")}</Text>
          ) : (
            <List
              dataSource={localAvailable}
              renderItem={(instance) => (
                <List.Item>
                  <Checkbox
                    checked={selectedIds.includes(instance.instance_id)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        instance.instance_id,
                        e.target.checked
                      )
                    }
                  >
                    <div className="instance-info">
                      <div className="instance-id">
                        {instance.instance_id}
                        <div
                          className={`healthy-status ${
                            instance.healthy === 1 ? "healthy" : "not-healthy"
                          }`}
                        ></div>
                      </div>
                      <div className="instance-details">
                        <div className="detail-item">
                          <span className="label">{t("Template Name")}:</span>
                          <span className="value">
                            {instance.template_name}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="label">{t("GPU Model")}:</span>
                          <span className="value">{instance.product_name}</span>
                        </div>
                      </div>
                    </div>
                  </Checkbox>
                </List.Item>
              )}
            />
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            className="primary-btn"
            onClick={moveToCluster}
            disabled={selectedIds.length === 0}
          >
            &gt;
          </button>
        </div>

        <div style={{ flex: 1 }}>
          <Title level={5}>{clusterName}</Title>
          {localSelected.length === 0 ? (
            <Text type="secondary">{t("No Data Yet")}</Text>
          ) : (
            <List
              dataSource={localSelected}
              renderItem={(instance) => (
                <List.Item
                  actions={
                    localSelected.some(
                      (i) => i.instance_id === instance.instance_id
                    )
                      ? [
                          <Button
                            type="link"
                            danger
                            onClick={() =>
                              removeLocalSelected(instance.instance_id)
                            }
                          >
                            {t("Delete")}
                          </Button>,
                        ]
                      : []
                  }
                >
                  <List.Item.Meta
                    title={
                      <div className="instance-info">
                        <div className="instance-id">
                          {instance.instance_id}
                          <div
                            className={`healthy-status ${
                              instance.healthy === 1 ? "healthy" : "not-healthy"
                            }`}
                          ></div>
                        </div>
                        <div className="instance-details">
                          <div className="detail-item">
                            <span className="label">{t("Template Name")}:</span>
                            <span className="value">
                              {instance.template_name}
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="label">{t("GPU Model")}:</span>
                            <span className="value">
                              {instance.product_name}
                            </span>
                          </div>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
